import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { HeroBackdrop } from '#app/components/hero-backdrop.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Card } from '#app/components/ui/card.tsx'
import {
	ChapterStage,
	chapterNumber,
} from '#app/components/ui/chapter-stage.tsx'
import { IconChip } from '#app/components/ui/icon-chip.tsx'
import { type IconName } from '#app/components/ui/icon.tsx'
import { PaintedText } from '#app/components/ui/painted-text.tsx'
import { Reveal } from '#app/components/ui/reveal.tsx'
import { WordReveal } from '#app/components/ui/word-reveal.tsx'
import { useLocalizedPath } from '#app/utils/i18n.ts'
import {
	generateMarketingMeta,
	generateNextcloudServiceJsonLd,
	getLayoutSeoData,
} from '#app/utils/seo.ts'
import { trackEvent } from '#app/utils/tracking.ts'
import { CtaBand } from './+marketing-components/cta-band.tsx'
import { type Route } from './+types/nextcloud.ts'

export const meta: Route.MetaFunction = ({ matches }) => {
	const { siteUrl, lang } = getLayoutSeoData(matches)
	return [
		...generateMarketingMeta({
			title: 'Managed Nextcloud für Organisationen | Open Sourced',
			description:
				'Managed Nextcloud Hosting mit Standort Schweiz für Organisationen: tägliche Backups, ein SLA mit fester Reaktionszeit, auf Wunsch Single Sign-on sowie Beratung und Entwicklung aus einer Hand.',
			siteUrl,
			lang,
			path: '/nextcloud',
		}),
		{ 'script:ld+json': generateNextcloudServiceJsonLd(siteUrl) },
	]
}

type IconItem = { icon: IconName; key: string }

const useCaseCards = [
	{ icon: 'file-text', key: 'documents' },
	{ icon: 'calendar', key: 'calendar' },
	{ icon: 'pencil-2', key: 'collabora' },
] as const satisfies readonly IconItem[]

const integrationCards = [
	{ icon: 'key', key: 'sso' },
	{ icon: 'avatar', key: 'groups' },
	{ icon: 'archive', key: 'teamFolders' },
	{ icon: 'code', key: 'development' },
] as const satisfies readonly IconItem[]

// The AVV item links to the document itself.
const operationItems = [
	{ icon: 'map-pin', key: 'location' },
	{ icon: 'reset', key: 'backups' },
	{ icon: 'lock-closed', key: 'encryption' },
	{ icon: 'passkey', key: 'twoFactor' },
	{ icon: 'activity-log', key: 'auditLogs' },
	{ icon: 'clock', key: 'monitoring' },
	{ icon: 'file-text', key: 'dpa', to: '/dpa' },
] as const satisfies readonly (IconItem & { to?: string })[]

// One icon per Vorgehen step, in the order of `nextcloud.process.steps`.
const processIcons = [
	'avatar',
	'file-text',
	'layers',
	'server',
] as const satisfies readonly IconName[]

const layerCards = [
	{ icon: 'server', key: 'hosting' },
	{ icon: 'update', key: 'support' },
	{ icon: 'shield-check', key: 'sla' },
] as const satisfies readonly IconItem[]

function SectionIntro({
	heading,
	subheading,
	paintedSubheading = false,
}: {
	heading: string
	subheading?: string
	/** The page's one lead painted on scroll. */
	paintedSubheading?: boolean
}) {
	return (
		<div className="mx-auto mb-12 max-w-3xl text-center">
			<h2 className="text-h2">
				<WordReveal text={heading} />
			</h2>
			{!subheading ? null : paintedSubheading ? (
				<PaintedText
					text={subheading}
					className="text-h4 mt-6 font-medium tracking-tight"
				/>
			) : (
				<Reveal delay={100}>
					<p className="text-body-lg text-muted-foreground mt-4">
						{subheading}
					</p>
				</Reveal>
			)}
		</div>
	)
}

function FeatureCardGrid({
	cards,
	className,
}: {
	cards: Array<{ icon: IconName; title: string; description: string }>
	className: string
}) {
	return (
		<div className={`grid grid-cols-1 gap-6 ${className}`}>
			{cards.map((card, i) => (
				<Reveal key={card.title} delay={i * 100}>
					<Card className="h-full gap-0 p-6">
						<IconChip icon={card.icon} size="lg" className="mb-4" />
						<h3 className="text-h5 mb-2">{card.title}</h3>
						<p className="text-body-sm text-muted-foreground">
							{card.description}
						</p>
					</Card>
				</Reveal>
			))}
		</div>
	)
}

export default function NextcloudRoute() {
	const localePath = useLocalizedPath()
	const { t } = useTranslation()
	const offerPath = `${localePath('/contact')}?anliegen=nextcloud`

	const processSteps = t('nextcloud.process.steps', {
		returnObjects: true,
	}) as Array<{ title: string; description: string }>

	return (
		<>
			{/* Hero */}
			<section className="relative overflow-hidden pt-32 pb-24">
				<HeroBackdrop />

				<div className="relative z-10 container text-center">
					<h1 className="text-h2 md:text-h1 mx-auto max-w-3xl font-bold">
						<WordReveal
							text={t('nextcloud.heading')}
							wordClassName="text-gradient-brand"
						/>
					</h1>
					<Reveal delay={100}>
						<p className="text-body-md text-muted-foreground md:text-body-lg mx-auto mt-6 max-w-2xl">
							{t('nextcloud.subheading')}
						</p>
					</Reveal>
					<Reveal delay={200}>
						<div className="mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center">
							<Button asChild size="lg">
								<Link
									to={offerPath}
									onClick={() =>
										trackEvent('cta', 'click', 'request-offer-nextcloud-hero')
									}
								>
									{t('cta.requestOffer')}
								</Link>
							</Button>
						</div>
					</Reveal>
				</div>
			</section>

			{/* Use cases */}
			<section className="bg-band py-24">
				<div className="container">
					<SectionIntro
						heading={t('nextcloud.useCases.heading')}
						subheading={t('nextcloud.useCases.subheading')}
					/>
					<FeatureCardGrid
						cards={useCaseCards.map(({ icon, key }) => ({
							icon,
							title: t(`nextcloud.useCases.${key}.title`),
							description: t(`nextcloud.useCases.${key}.description`),
						}))}
						className="md:grid-cols-3"
					/>
				</div>
			</section>

			{/* Integration */}
			<section className="py-24">
				<div className="container">
					<SectionIntro
						heading={t('nextcloud.integration.heading')}
						subheading={t('nextcloud.integration.subheading')}
					/>
					<FeatureCardGrid
						cards={integrationCards.map(({ icon, key }) => ({
							icon,
							title: t(`nextcloud.integration.${key}.title`),
							description: t(`nextcloud.integration.${key}.description`),
						}))}
						className="sm:grid-cols-2 lg:grid-cols-4"
					/>
					<Reveal delay={200}>
						<p className="text-body-md text-muted-foreground mx-auto mt-10 max-w-2xl text-center">
							{t('nextcloud.integration.consulting')}
						</p>
					</Reveal>
				</div>
			</section>

			{/* Betrieb und Sicherheit */}
			<section className="bg-band py-24">
				<div className="container max-w-3xl">
					<SectionIntro
						heading={t('nextcloud.operations.heading')}
						subheading={t('nextcloud.operations.subheading')}
						paintedSubheading
					/>
					<ul className="space-y-4">
						{operationItems.map((item, i) => {
							const label = t(`nextcloud.operations.${item.key}`)
							return (
								<Card asChild key={item.key}>
									<li>
										<Reveal delay={i * 100} className="flex items-center gap-4">
											<IconChip icon={item.icon} />
											{'to' in item ? (
												<Button
													variant="link"
													className="text-body-md h-auto px-0 font-medium whitespace-normal"
													asChild
												>
													<Link to={localePath(item.to)}>{label}</Link>
												</Button>
											) : (
												<span className="text-body-md font-medium">
													{label}
												</span>
											)}
										</Reveal>
									</li>
								</Card>
							)
						})}
					</ul>
				</div>
			</section>

			{/* Hosting, Support, SLA */}
			<section className="py-24">
				<div className="container">
					<SectionIntro
						heading={t('nextcloud.layers.heading')}
						subheading={t('nextcloud.layers.subheading')}
					/>
					<FeatureCardGrid
						cards={layerCards.map(({ icon, key }) => ({
							icon,
							title: t(`nextcloud.layers.${key}.title`),
							description: t(`nextcloud.layers.${key}.description`),
						}))}
						className="md:grid-cols-3"
					/>
					<Reveal delay={200}>
						<p className="text-body-sm text-muted-foreground mx-auto mt-10 max-w-2xl text-center">
							{t('nextcloud.layers.note')}
						</p>
						<p className="mt-6 text-center">
							<Button
								variant="link"
								className="text-body-md h-auto px-0"
								asChild
							>
								<Link to={localePath('/sla')}>
									{t('nextcloud.layers.slaLink')}
								</Link>
							</Button>
						</p>
					</Reveal>
				</div>
			</section>

			{/* Vorgehen: one chapter per step, the panel pinned beside them */}
			<section className="py-24">
				<div className="container">
					<SectionIntro heading={t('nextcloud.process.heading')} />
					{Array.isArray(processSteps) ? (
						<ChapterStage
							indexLabel={t('nextcloud.process.heading')}
							headingLevel="h3"
							chapters={processSteps.map((step, i) => ({
								key: step.title,
								eyebrow: chapterNumber(i),
								title: step.title,
								lead: step.description,
								icons: [processIcons[i] ?? 'check'],
							}))}
						/>
					) : null}
				</div>
			</section>

			{/* Bottom CTA */}
			<CtaBand
				heading={t('nextcloud.ctaHeading')}
				text={t('nextcloud.ctaSubheading')}
				action={
					<Button asChild size="lg" variant="white">
						<Link
							to={offerPath}
							onClick={() =>
								trackEvent('cta', 'click', 'request-offer-nextcloud-footer')
							}
						>
							{t('cta.requestOffer')}
						</Link>
					</Button>
				}
			/>
		</>
	)
}
